class NavigationLinks extends HTMLElement {
  constructor() {
    super();
    this.nav = this.querySelector('.slider__grid');
    if (!this.nav) return;

    this.links = [...this.nav.querySelectorAll('.navigation-link')];
    this.linkTargets = this.links
      .map((link) => {
        const hash = link.hash || '';
        if (!hash || hash === '#') return null;

        const id = decodeURIComponent(hash.slice(1));
        const target = document.getElementById(id) || document.querySelector(hash);
        if (!target) return null;

        return { link, target };
      })
      .filter(Boolean);

    if (!this.linkTargets.length) return;

    this.currentLink = null;
    this.scrollTicking = false;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.syncActiveLink = this.syncActiveLink.bind(this);

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('hashchange', this.syncActiveLink);
    window.addEventListener('on:breakpoint-change', this.handleResize);
    document.addEventListener('shopify:section:load', this.handleResize);

    this.updateNavProps(this.linkTargets[0].link, true);
    requestAnimationFrame(() => {
      this.nav.classList.add('is-underline-ready');
      this.syncActiveLink(true);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('hashchange', this.syncActiveLink);
    window.removeEventListener('on:breakpoint-change', this.handleResize);
    document.removeEventListener('shopify:section:load', this.handleResize);
    this.nav.classList.remove('is-underline-ready');
  }

  getTopOffset() {
    let offset = 0;

    const header = document.querySelector('.cc-header');
    if (header) {
      const style = window.getComputedStyle(header);
      const rect = header.getBoundingClientRect();
      const isPinnedSticky = style.position === 'sticky' && rect.top <= 0;
      if (style.position === 'fixed' || isPinnedSticky) {
        offset += header.offsetHeight;
      }
    }

    const navSection = this.closest('.cc-navigation-links');
    if (navSection) {
      const style = window.getComputedStyle(navSection);
      const isSticky = style.position === 'sticky' || style.position === '-webkit-sticky' || style.position === 'fixed';
      const isVisible = style.display !== 'none' && navSection.offsetParent !== null;
      if (isSticky && isVisible) {
        offset += navSection.offsetHeight;
      }
    }

    return offset + 8;
  }

  handleScroll() {
    if (this.scrollTicking) return;
    this.scrollTicking = true;
    requestAnimationFrame(() => {
      this.scrollTicking = false;
      this.syncActiveLink();
    });
  }

  handleResize() {
    this.syncActiveLink(true);
  }

  syncActiveLink(force = false) {
    if (!this.linkTargets.length) return;

    const marker = this.getTopOffset();
    let active = this.linkTargets[0];

    for (const item of this.linkTargets) {
      const top = item.target.getBoundingClientRect().top;
      if (top - marker <= 0) {
        active = item;
      } else {
        break;
      }
    }

    const atPageBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
    if (atPageBottom) {
      active = this.linkTargets[this.linkTargets.length - 1];
    }

    this.updateNavProps(active.link, force);
  }

  updateNavProps(elem, force = false) {
    if (!elem) return;
    if (!force && this.currentLink === elem) return;

    this.currentLink = elem;
    this.nav.style.setProperty('--underline-width', `${elem.offsetWidth}px`);
    this.nav.style.setProperty('--underline-offset-x', `${elem.offsetLeft}px`);
    // Keep the document position unchanged; only center the active link within
    // the horizontal navigation strip.
    this.nav.scrollTo({
      left: elem.offsetLeft - ((this.nav.clientWidth - elem.offsetWidth) / 2),
      behavior: 'auto'
    });
  }
}

if (!customElements.get('navigation-links')) {
  customElements.define('navigation-links', NavigationLinks);
}
